// src/components/customers/AssignedDriversBar.jsx
import { useEffect, useMemo, useRef, useState } from "react";
import {
  addDirectDriverToJob,
  removeDirectDriverFromJob,
} from "../../services/jobService";
import { getDrivers } from "../../services/driverService";

/**
 * AssignedDriversBar
 *
 * Renders inside the "Areas & Measurements" card on ViewCustomerRooms.
 * Shows the drivers who currently have access to this job, with two
 * sources:
 *
 *  - `assignedDriverIds` (auto-derived from calendar events) — rendered
 *    as pills with a small calendar icon. Read-only here; admin removes
 *    them by unassigning the underlying calendar event.
 *
 *  - `directDriverIds` (admin-managed via this bar) — rendered as pills
 *    with an X button. Admin adds via the "+ Assign driver" dropdown and
 *    removes via the X.
 *
 * Either array grants the driver Firestore read access to the job +
 * write access to the `rooms` field. Sprint 2.6 design.
 *
 * @param {Object}   props
 * @param {Object}   props.job              the job document (must have id)
 * @param {Function} props.onChange          called after every
 *   add/remove with the new arrays so the parent can refresh local state
 *   without a full re-fetch
 */
const AssignedDriversBar = ({ job, onChange }) => {
  const [allDrivers, setAllDrivers] = useState([]);
  const [busyUid, setBusyUid] = useState(null);
  const [addMenuOpen, setAddMenuOpen] = useState(false);
  const [error, setError] = useState(null);
  const menuRef = useRef(null);

  // Fetch the active drivers list once on mount. Disabled drivers are
  // excluded from the "Add" dropdown but still rendered as pills if they
  // appear on the job (with a "disabled" badge styling).
  useEffect(() => {
    let cancelled = false;
    getDrivers({ includeDisabled: true })
      .then((list) => {
        if (!cancelled) setAllDrivers(list);
      })
      .catch((err) => {
        if (!cancelled) setError(err.message || String(err));
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // Close the add-menu when the user clicks outside.
  useEffect(() => {
    if (!addMenuOpen) return;
    const onDoc = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) {
        setAddMenuOpen(false);
      }
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [addMenuOpen]);

  const driversById = useMemo(() => {
    const m = {};
    allDrivers.forEach((d) => {
      m[d.id] = d;
    });
    return m;
  }, [allDrivers]);

  const assignedDriverIds = job?.assignedDriverIds || [];
  const directDriverIds = job?.directDriverIds || [];

  // Deduplicate — a driver might appear in BOTH arrays (event + manual);
  // we render them once but treat any membership in directDriverIds as
  // "removable from here", since unassigning here only touches that array.
  const allAssignedIds = useMemo(
    () => Array.from(new Set([...assignedDriverIds, ...directDriverIds])),
    [assignedDriverIds, directDriverIds]
  );

  // Drivers eligible to be added: active, not already in either array.
  const addable = useMemo(
    () =>
      allDrivers.filter(
        (d) => !d.disabled && !allAssignedIds.includes(d.id)
      ),
    [allDrivers, allAssignedIds]
  );

  const handleAdd = async (uid) => {
    setBusyUid(uid);
    setError(null);
    try {
      await addDirectDriverToJob(job.id, uid);
      const nextDirect = Array.from(new Set([...directDriverIds, uid]));
      onChange?.({ directDriverIds: nextDirect });
    } catch (err) {
      setError(err.message || String(err));
    } finally {
      setBusyUid(null);
      setAddMenuOpen(false);
    }
  };

  const handleRemove = async (uid) => {
    setBusyUid(uid);
    setError(null);
    try {
      await removeDirectDriverFromJob(job.id, uid);
      const nextDirect = directDriverIds.filter((id) => id !== uid);
      onChange?.({ directDriverIds: nextDirect });
    } catch (err) {
      setError(err.message || String(err));
    } finally {
      setBusyUid(null);
    }
  };

  return (
    <div className="mb-4">
      <div className="flex items-center flex-wrap gap-2">
        <span className="text-sm font-medium text-gray-700 mr-1">
          Fitters on this job:
        </span>

        {allAssignedIds.length === 0 && (
          <span className="text-sm text-gray-500 italic">
            no fitters assigned yet
          </span>
        )}

        {allAssignedIds.map((uid) => {
          const d = driversById[uid];
          const isDirect = directDriverIds.includes(uid);
          const isFromEvent = assignedDriverIds.includes(uid);
          const isDisabled = d?.disabled;
          const label = d?.name || `Fitter ${uid.slice(0, 6)}…`;
          return (
            <span
              key={uid}
              className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-sm font-medium ${
                isDisabled
                  ? "bg-gray-200 text-gray-600"
                  : "bg-emerald-100 text-emerald-800"
              }`}
              title={
                isDirect && isFromEvent
                  ? "Direct + via calendar event"
                  : isFromEvent
                  ? "Via calendar event — remove by unassigning the event"
                  : "Direct assignment"
              }
            >
              {/* Truck icon */}
              <svg
                className="w-3.5 h-3.5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M3 7a1 1 0 011-1h9a1 1 0 011 1v9H3V7zm11 3h4l3 3v3h-7v-6zM5 19a2 2 0 100-4 2 2 0 000 4zm12 0a2 2 0 100-4 2 2 0 000 4z"
                />
              </svg>
              <span>{label}</span>

              {/* Calendar tag if event-derived */}
              {isFromEvent && (
                <svg
                  className="w-3 h-3 opacity-70"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                  title="From calendar event"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
                  />
                </svg>
              )}

              {/* Remove button only for direct assignments */}
              {isDirect && (
                <button
                  type="button"
                  onClick={() => handleRemove(uid)}
                  disabled={busyUid === uid}
                  className="ml-0.5 -mr-0.5 p-0.5 rounded-full hover:bg-emerald-200 disabled:opacity-50"
                  title="Remove direct assignment"
                  aria-label={`Remove ${label} from this job`}
                >
                  <svg
                    className="w-3.5 h-3.5"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2.5}
                      d="M6 18L18 6M6 6l12 12"
                    />
                  </svg>
                </button>
              )}
            </span>
          );
        })}

        {/* Assign-driver dropdown trigger */}
        <div className="relative" ref={menuRef}>
          <button
            type="button"
            onClick={() => setAddMenuOpen((o) => !o)}
            disabled={addable.length === 0}
            className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-sm font-medium border border-dashed border-gray-300 text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            title={
              addable.length === 0
                ? "All active fitters already assigned"
                : "Assign a fitter to this job"
            }
          >
            <svg
              className="w-4 h-4"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 4v16m8-8H4"
              />
            </svg>
            Assign fitter
          </button>

          {addMenuOpen && addable.length > 0 && (
            <div className="absolute z-20 mt-2 w-64 max-h-72 overflow-auto bg-white border border-gray-200 rounded-lg shadow-lg">
              <ul className="py-1">
                {addable.map((d) => (
                  <li key={d.id}>
                    <button
                      type="button"
                      onClick={() => handleAdd(d.id)}
                      disabled={busyUid === d.id}
                      className="w-full text-left px-3 py-2 text-sm hover:bg-gray-50 flex items-center gap-2 disabled:opacity-50"
                    >
                      <span className="flex-1">
                        <span className="font-medium text-gray-900">
                          {d.name}
                        </span>
                        {d.licensePlate && (
                          <span className="ml-1 text-xs text-gray-500">
                            · {d.licensePlate}
                          </span>
                        )}
                      </span>
                      <svg
                        className="w-4 h-4 text-gray-400"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M12 4v16m8-8H4"
                        />
                      </svg>
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      </div>

      {error && (
        <p className="mt-2 text-sm text-red-600">
          Couldn't update fitters: {error}
        </p>
      )}

      {/* Caveat to admin — fitters added here get visibility but no event */}
      {directDriverIds.length > 0 && (
        <p className="mt-2 text-xs text-gray-500">
          Direct assignments grant access to view + add measurements. They
          won't appear as cards in the fitter's app unless also assigned to
          a calendar event on this job.
        </p>
      )}
    </div>
  );
};

export default AssignedDriversBar;
